import { useContext, useState } from 'react';
import {
    Typography, Grid, Box,
    ToggleButton, ToggleButtonGroup,
    FormControl, InputLabel, Select, MenuItem,
    Button, Dialog, DialogTitle, DialogContent,
    Snackbar, Alert, Stack, Divider
} from '@mui/material';
import CardInfoDialog from './CardInfoDialog';
import { UserContext } from '../contexts/UserContext';
import { rarityXpMultiplier, getCardStats } from '../utilities';
import { PlayingCard } from './PlayingCard';

// 🔥 Firebase
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const DEFENSE_LIMIT = 5;
const ATTACK_LIMIT = 5;

const TOKENS = {
    panel: 'rgba(11,11,11,0.9)',
    panelSoft: 'rgba(0,0,0,0.7)',
    gold: '#d4af37',
    goldSoft: 'rgba(212,175,55,0.75)',
    goldDim: 'rgba(212,175,55,0.6)',
    text: '#f9f6e3',
    border: '1px solid rgba(212,175,55,0.25)',
    shadow: '0 0 28px rgba(212,175,55,0.18), inset 0 0 18px rgba(212,175,55,0.06)',
    chipBg: 'rgba(212,175,55,0.12)',
};

function SectionHeader({ children, divider = true }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography
                variant="h5"
                sx={{
                    fontFamily: 'Cinzel, serif',
                    fontWeight: 700,
                    letterSpacing: '0.8px',
                    color: TOKENS.gold,
                    textShadow: '0 0 10px rgba(212,175,55,0.3)',
                    mb: 1,
                }}
            >
                {children}
            </Typography>
            {divider && (
                <Divider
                    sx={{
                        borderColor: 'rgba(212,175,55,0.25)',
                        mb: 1.5,
                    }}
                />
            )}
        </Box>
    );
}

function StatBar({ labelLeft, valueLeft, labelRight, valueRight }) {
    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                background: TOKENS.panel,
                border: TOKENS.border,
                boxShadow: TOKENS.shadow,
                borderRadius: 2,
                p: 1.25,
                mb: 2,
            }}
        >
            <Typography sx={{ color: TOKENS.text }}>
                <strong>{labelLeft}:</strong> {valueLeft}
            </Typography>
            <Typography sx={{ color: TOKENS.text }}>
                <strong>{labelRight}:</strong> {valueRight}
            </Typography>
        </Box>
    );
}

function GoldButton(props) {
    return (
        <Button
            {...props}
            variant={props.variant || 'contained'}
            sx={{
                fontFamily: `'Cinzel', serif`,
                letterSpacing: '0.6px',
                background:
                    'linear-gradient(180deg, rgba(212,175,55,0.9) 0%, rgba(212,175,55,0.75) 100%)',
                color: '#120d00',
                border: '1px solid rgba(212,175,55,0.45)',
                boxShadow: '0 0 18px rgba(212,175,55,0.25)',
                '&:hover': {
                    boxShadow: '0 0 26px rgba(212,175,55,0.4)',
                    filter: 'brightness(1.05)',
                },
                ...(props.sx || {}),
            }}
        />
    );
}

function FilterBar({
    filterField, setFilterField,
    sortOrder, setSortOrder,
    classificationFilter, setClassificationFilter,
    rarityFilter, setRarityFilter,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                mb: 2,
                p: 1.5,
                background: TOKENS.panelSoft,
                border: TOKENS.border,
                boxShadow: TOKENS.shadow,
                borderRadius: 2,
            }}
        >
            <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ color: TOKENS.goldDim }}>Sort By</InputLabel>
                <Select
                    value={filterField}
                    label="Sort By"
                    onChange={(e) => setFilterField(e.target.value)}
                    sx={{
                        color: TOKENS.text,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.25)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.goldDim },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.gold },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                background: '#0b0b0b',
                                color: TOKENS.text,
                                border: TOKENS.border,
                            },
                        },
                    }}
                >
                    <MenuItem value="attack">Attack</MenuItem>
                    <MenuItem value="defense">Defense</MenuItem>
                    <MenuItem value="abilityPercent">Ability %</MenuItem>
                    <MenuItem value="abilityFlat">Ability Flat</MenuItem>
                    <MenuItem value="rarity">Rarity</MenuItem>
                </Select>
            </FormControl>

            <ToggleButtonGroup
                value={sortOrder}
                exclusive
                onChange={(_, v) => v && setSortOrder(v)}
                size="small"
                sx={{
                    background: 'rgba(212,175,55,0.08)',
                    borderRadius: 2,
                    border: TOKENS.border,
                    '& .MuiToggleButton-root': {
                        color: TOKENS.text,
                        borderColor: 'rgba(212,175,55,0.25)',
                        '&.Mui-selected': {
                            backgroundColor: TOKENS.chipBg,
                            color: TOKENS.gold,
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(212,175,55,0.12)',
                        },
                    },
                }}
            >
                <ToggleButton value="desc">High → Low</ToggleButton>
                <ToggleButton value="asc">Low → High</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: TOKENS.goldDim }}>Classification</InputLabel>
                <Select
                    value={classificationFilter}
                    label="Classification"
                    onChange={(e) => setClassificationFilter(e.target.value)}
                    sx={{
                        color: TOKENS.text,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.25)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.goldDim },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.gold },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                background: '#0b0b0b',
                                color: TOKENS.text,
                                border: TOKENS.border,
                            },
                        },
                    }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Man">Man</MenuItem>
                    <MenuItem value="Beast">Beast</MenuItem>
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: TOKENS.goldDim }}>Rarity</InputLabel>
                <Select
                    value={rarityFilter}
                    label="Rarity"
                    onChange={(e) => setRarityFilter(e.target.value)}
                    sx={{
                        color: TOKENS.text,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.25)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.goldDim },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.gold },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                background: '#0b0b0b',
                                color: TOKENS.text,
                                border: TOKENS.border,
                            },
                        },
                    }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Common">Common</MenuItem>
                    <MenuItem value="Uncommon">Uncommon</MenuItem>
                    <MenuItem value="Rare">Rare</MenuItem>
                    <MenuItem value="High Rare">High Rare</MenuItem>
                    <MenuItem value="Super Rare">Super Rare</MenuItem>
                    <MenuItem value="Super Super Rare">Super Super Rare</MenuItem>
                    <MenuItem value="Legendary">Legendary</MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
}

function CardManager() {
    const {
        globalCards,
        userData,
        updateCardActivity,
        updateDefenseActivity,
        updateActiveOrder,   // NEW
        updateDefenseOrder,  // NEW
    } = useContext(UserContext);

    const [selectedCard, setSelectedCard] = useState(null);
    const [filterField, setFilterField] = useState('attack');
    const [sortOrder, setSortOrder] = useState('desc');
    const [classificationFilter, setClassificationFilter] = useState('all');
    const [rarityFilter, setRarityFilter] = useState('all');

    const fmt = (n) => Number(n ?? 0).toLocaleString();

    const [attackDialogOpen, setAttackDialogOpen] = useState(false);
    const [defenseDialogOpen, setDefenseDialogOpen] = useState(false);

    const [snack, setSnack] = useState({ open: false, message: '' });

    const handleOpenDialog = (card) => {
        setAttackDialogOpen(false);
        setDefenseDialogOpen(false);
        setSelectedCard(card);
    };
    const handleCloseDialog = () => setSelectedCard(null);
    const openSnack = (message) => setSnack({ open: true, message });
    const closeSnack = () => setSnack({ open: false, message: '' });

    if (!userData) {
        return (
            <Typography sx={{ color: TOKENS.text, opacity: 0.85 }}>
                Loading...
            </Typography>
        );
    }

    // Compose full cards with computed stats & evolution visuals
    const cards = (userData.ownedCards || [])
        .map((instance, ownedIndex) => {
            const baseCard = globalCards[instance.cardId];
            if (!baseCard) return null;
            const { attack, defense } = getCardStats(instance, baseCard);
            const evolutionData = baseCard.evolutions?.[instance.evolutionStage] || {};
            return {
                ...baseCard,
                ...instance,
                attack,
                defense,
                image: evolutionData.image || baseCard.image,
                rarity: evolutionData.rarity || baseCard.rarity,
                ability: evolutionData.ability || baseCard.ability,
                abilityDescription: evolutionData.abilityDescription || baseCard.abilityDescription,
                ownedIndex, // stable fallback order
            };
        })
        .filter(Boolean);

    // NEW: shared helper for ordering by deck slot
    const sortByOrder = (a, b, field) => {
        const ao = typeof a[field] === 'number' ? a[field] : 999;
        const bo = typeof b[field] === 'number' ? b[field] : 999;
        if (ao !== bo) return ao - bo;
        return (a.ownedIndex ?? 0) - (b.ownedIndex ?? 0);
    };

    const activeCards = cards
        .filter((c) => c.active)
        .sort((a, b) => sortByOrder(a, b, 'activeOrder'));

    const defenseCards = cards
        .filter((c) => c.defenseActive)
        .sort((a, b) => sortByOrder(a, b, 'defenseOrder'));

    const ownedCards = cards.filter((c) => !c.active);

    const totalAttack = activeCards.reduce((sum, c) => sum + c.attack, 0);
    const totalDefense = activeCards.reduce((sum, c) => sum + c.defense, 0);

    const defenseTotalAttack = defenseCards.reduce((sum, c) => sum + c.attack, 0);
    const defenseTotalDefense = defenseCards.reduce((sum, c) => sum + c.defense, 0);

    // --- helper: resolve guild name from guildId ---
    const resolveGuildName = async () => {
        const guildId = userData?.guildId;
        if (!guildId) return 'No Guild';

        try {
            // 🔥 Adjust 'guilds' to your actual collection name if different
            const guildRef = doc(db, 'guilds', guildId);
            const guildSnap = await getDoc(guildRef);

            if (!guildSnap.exists()) {
                console.warn('[CardManager] Guild not found for guildId:', guildId);
                return 'No Guild';
            }

            const guildData = guildSnap.data();
            return guildData.name || guildData.guildName || 'No Guild';
        } catch (err) {
            console.error('[CardManager] Failed to resolve guild name:', err);
            return 'No Guild';
        }
    };

    // --- public mirror sync ---
    const buildPublicDefenseDeck = (list) =>
        list.slice(0, DEFENSE_LIMIT).map(({
            cardId,
            level,
            evolutionStage,
            ascensions,
            defenseOrder,
        }) => ({
            cardId,
            level: Number(level || 1),
            evolutionStage: Number(evolutionStage || 0),
            // ✅ include ascensions so publicUsers + battle requests see the true cap/stats
            ascensions: typeof ascensions === 'number'
                ? ascensions
                : Number(ascensions || 0),
            defenseOrder: typeof defenseOrder === 'number' ? defenseOrder : null,
        }));

    const syncPublicProfile = async (nextDefenseCards) => {
        const uid = auth?.currentUser?.uid || userData?.uid;
        if (!uid) return;

        const defenseDeckPublic = buildPublicDefenseDeck(nextDefenseCards);
        const guildName = await resolveGuildName();

        await setDoc(
            doc(db, 'publicUsers', uid),
            {
                username: userData.username || 'Opponent',
                guildName,
                league: userData.league || 'Unranked',
                trophies: Number(userData.trophies || 0),
                defenseDeckPublic,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    };

    const findByInstanceId = (id) => cards.find((c) => c.instanceId === id);

    const handleAddToDeck = (instanceId) => {
        if (activeCards.length >= ATTACK_LIMIT) {
            openSnack(`Attack deck is limited to ${ATTACK_LIMIT} cards.`);
            return;
        }
        updateCardActivity(instanceId, true);
        // Note: activeOrder is user-controlled; we don't auto-assign here.
    };
    const handleRemoveFromDeck = (instanceId) => updateCardActivity(instanceId, false);

    const handleAddToDefenseDeck = async (instanceId) => {
        if (defenseCards.length >= DEFENSE_LIMIT) {
            openSnack(`Defense deck is limited to ${DEFENSE_LIMIT} cards.`);
            return;
        }
        updateDefenseActivity(instanceId, true);

        const cardToAdd = findByInstanceId(instanceId);
        const nextDefense = cardToAdd ? [...defenseCards, cardToAdd] : defenseCards;
        try {
            await syncPublicProfile(nextDefense);
        } catch (e) {
            console.error('[CardManager] sync public (add) failed:', e);
        }
    };

    const handleRemoveFromDefenseDeck = async (instanceId) => {
        updateDefenseActivity(instanceId, false);

        const nextDefense = defenseCards.filter((c) => c.instanceId !== instanceId);
        try {
            await syncPublicProfile(nextDefense);
        } catch (e) {
            console.error('[CardManager] sync public (remove) failed:', e);
        }
    };

    const getAbilityValue = (card, type = 'percent') => {
        if (!Array.isArray(card.ability)) return 0;
        return card.ability.reduce((sum, a) => sum + (a[type] || 0), 0);
    };

    const rarityRank = {
        Common: 1,
        Uncommon: 2,
        Rare: 3,
        'High Rare': 4,
        'Super Rare': 5,
        'Super Super Rare': 6,
        Legendary: 7,
    };

    const filteredAndSortedOwnedCards = [...ownedCards]
        .filter((card) => {
            if (classificationFilter !== 'all' && card.classification !== classificationFilter) return false;
            if (rarityFilter !== 'all' && card.rarity !== rarityFilter) return false;
            return true;
        })
        .sort((a, b) => {
            let aVal, bVal;
            switch (filterField) {
                case 'defense':
                    aVal = a.defense; bVal = b.defense; break;
                case 'abilityPercent':
                    aVal = getAbilityValue(a, 'percent'); bVal = getAbilityValue(b, 'percent'); break;
                case 'abilityFlat':
                    aVal = getAbilityValue(a, 'flatAmount'); bVal = getAbilityValue(b, 'flatAmount'); break;
                case 'rarity':
                    aVal = rarityRank[a.rarity] || 0; bVal = rarityRank[b.rarity] || 0; break;
                case 'attack':
                default:
                    aVal = a.attack; bVal = b.attack;
            }
            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

    const renderCard = (card, index, size = 'md') => (
        <Grid item key={`${card.id || 'card'}-${index}`}>
            <PlayingCard
                showStats="True"
                card={card}
                showLevel
                size={size}
                onClick={() => handleOpenDialog(card)}
            />
        </Grid>
    );

    function DeckRow({ cards, onCardClick }) {
        const row = cards.slice(0, 5);
        return (
            <Box
                sx={{
                    background: 'linear-gradient(180deg, #060606 0%, #0b0b0b 100%)',
                    border: TOKENS.border,
                    boxShadow: TOKENS.shadow,
                    borderRadius: 2,
                    p: 1.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                    gap: 2,
                    alignItems: 'stretch',
                    mb: 3,
                }}
            >
                {row.map((card, i) => (
                    <Box
                        key={card.instanceId || card.id || `card-${i}`}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            transform: i === 0 ? 'scale(1.02)' : 'none',
                            transformOrigin: 'center',
                            filter: i === 0 ? 'drop-shadow(0 0 12px rgba(212,175,55,0.25))' : 'none',
                        }}
                    >
                        <PlayingCard
                            showStats="True"
                            card={card}
                            showLevel
                            size="md"
                            onClick={() => onCardClick?.(card)}
                        />
                    </Box>
                ))}
                {Array.from({ length: Math.max(0, 5 - row.length) }).map((_, idx) => (
                    <Box key={`empty-${idx}`} />
                ))}
            </Box>
        );
    }

    return (
        <>
            {/* Attack Deck */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SectionHeader divider={false}>Attack Deck</SectionHeader>
                <GoldButton onClick={() => setAttackDialogOpen(true)}>
                    Edit Attack Deck
                </GoldButton>
            </Box>

            <StatBar
                labelLeft="Total Attack"
                valueLeft={fmt(totalAttack)}
                labelRight="Total Defense"
                valueRight={fmt(totalDefense)}
            />

            {activeCards.length === 0 ? (
                <Typography sx={{ color: TOKENS.text, opacity: 0.9 }}>
                    No cards in attack deck.
                </Typography>
            ) : (
                <>
                    <DeckRow cards={activeCards} onCardClick={handleOpenDialog} />

                    {/* Attack Deck order controls */}
                    <Box
                        sx={{
                            mt: 1,
                            mb: 3,
                            p: 1.5,
                            background: TOKENS.panelSoft,
                            border: TOKENS.border,
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: TOKENS.text,
                                mb: 1,
                                fontFamily: `'Cinzel', serif`,
                                letterSpacing: '.6px',
                            }}
                        >
                            Deck Order — <strong>1</strong> has the highest ability trigger chance.
                        </Typography>

                        <Grid container spacing={1}>
                            {activeCards.map((card) => (
                                <Grid item xs={12} sm={6} md={4} key={card.instanceId}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            sx={{
                                                color: TOKENS.text,
                                                flexGrow: 1,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {card.name}
                                        </Typography>
                                        <FormControl size="small" sx={{ minWidth: 80 }}>
                                            <InputLabel sx={{ color: TOKENS.goldDim }}>Slot</InputLabel>
                                            <Select
                                                value={card.activeOrder ?? ''}
                                                label="Slot"
                                                onChange={(e) =>
                                                    updateActiveOrder(
                                                        card.instanceId,
                                                        Number(e.target.value)
                                                    )
                                                }
                                                sx={{
                                                    color: TOKENS.text,
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(212,175,55,0.25)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TOKENS.goldDim,
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TOKENS.gold,
                                                    },
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            background: '#0b0b0b',
                                                            color: TOKENS.text,
                                                            border: TOKENS.border,
                                                        },
                                                    },
                                                }}
                                            >
                                                {[1, 2, 3, 4, 5].map((slot) => (
                                                    <MenuItem key={slot} value={slot}>
                                                        {slot}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </>
            )}

            {/* Defense Deck */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SectionHeader divider={false}>Defense Deck</SectionHeader>
                <GoldButton onClick={() => setDefenseDialogOpen(true)}>
                    Edit Defense Deck
                </GoldButton>
            </Box>

            <StatBar
                labelLeft="Total Attack"
                valueLeft={fmt(defenseTotalAttack)}
                labelRight="Total Defense"
                valueRight={fmt(defenseTotalDefense)}
            />

            {defenseCards.length === 0 ? (
                <Typography sx={{ color: TOKENS.text, opacity: 0.9 }}>
                    No cards in defense deck.
                </Typography>
            ) : (
                <>
                    <DeckRow cards={defenseCards} onCardClick={handleOpenDialog} />

                    {/* Defense Deck order controls */}
                    <Box
                        sx={{
                            mt: 1,
                            mb: 3,
                            p: 1.5,
                            background: TOKENS.panelSoft,
                            border: TOKENS.border,
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: TOKENS.text,
                                mb: 1,
                                fontFamily: `'Cinzel', serif`,
                                letterSpacing: '.6px',
                            }}
                        >
                            Defense Order — <strong>1</strong> has the highest ability trigger chance when
                            your deck defends.
                        </Typography>

                        <Grid container spacing={1}>
                            {defenseCards.map((card) => (
                                <Grid item xs={12} sm={6} md={4} key={card.instanceId}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            sx={{
                                                color: TOKENS.text,
                                                flexGrow: 1,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {card.name}
                                        </Typography>
                                        <FormControl size="small" sx={{ minWidth: 80 }}>
                                            <InputLabel sx={{ color: TOKENS.goldDim }}>Slot</InputLabel>
                                            <Select
                                                value={card.defenseOrder ?? ''}
                                                label="Slot"
                                                onChange={async (e) => {
                                                    const newSlot = Number(e.target.value);

                                                    updateDefenseOrder(card.instanceId, newSlot);

                                                    const nextDefense = defenseCards.map((c) =>
                                                        c.instanceId === card.instanceId
                                                            ? { ...c, defenseOrder: newSlot }
                                                            : c
                                                    );

                                                    try {
                                                        await syncPublicProfile(nextDefense);
                                                    } catch (err) {
                                                        console.error('[CardManager] sync public (order change) failed:', err);
                                                    }
                                                }}
                                                sx={{
                                                    color: TOKENS.text,
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(212,175,55,0.25)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TOKENS.goldDim,
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TOKENS.gold,
                                                    },
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            background: '#0b0b0b',
                                                            color: TOKENS.text,
                                                            border: TOKENS.border,
                                                        },
                                                    },
                                                }}
                                            >
                                                {[1, 2, 3, 4, 5].map((slot) => (
                                                    <MenuItem key={slot} value={slot}>
                                                        {slot}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </>
            )}

            {/* Owned */}
            <SectionHeader>Owned Cards</SectionHeader>

            <FilterBar
                filterField={filterField}
                setFilterField={setFilterField}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                classificationFilter={classificationFilter}
                setClassificationFilter={setClassificationFilter}
                rarityFilter={rarityFilter}
                setRarityFilter={setRarityFilter}
            />

            <Grid container spacing={2} alignItems="stretch">
                {filteredAndSortedOwnedCards.map((card, index) => renderCard(card, index, 'sm'))}
            </Grid>

            {/* Shared Card Info Dialog */}
            <Dialog
                open={!!selectedCard}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        background: '#0b0b0b',
                        color: TOKENS.text,
                        border: TOKENS.border,
                        boxShadow: TOKENS.shadow,
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '0.6px',
                        color: TOKENS.gold,
                    }}
                >
                    Card Details
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(212,175,55,0.2)' }}>
                    <CardInfoDialog
                        open
                        onClose={handleCloseDialog}
                        card={selectedCard}
                        onAddToDeck={() => selectedCard && handleAddToDeck(selectedCard.instanceId)}
                        onRemoveFromDeck={() => selectedCard && handleRemoveFromDeck(selectedCard.instanceId)}
                        onAddToDefenseDeck={() => selectedCard && handleAddToDefenseDeck(selectedCard.instanceId)}
                        onRemoveFromDefenseDeck={() => selectedCard && handleRemoveFromDefenseDeck(selectedCard.instanceId)}
                    />
                </DialogContent>
            </Dialog>

            {/* Edit Attack Deck Dialog (NEW) */}
            <Dialog
                open={attackDialogOpen}
                onClose={() => setAttackDialogOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        background: '#0b0b0b',
                        color: TOKENS.text,
                        border: TOKENS.border,
                        boxShadow: TOKENS.shadow,
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '0.6px',
                        color: TOKENS.gold,
                    }}
                >
                    Edit Attack Deck
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(212,175,55,0.2)' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: TOKENS.text }}>
                            Pick up to {ATTACK_LIMIT} cards. Current: {activeCards.length}/{ATTACK_LIMIT} —{' '}
                            <strong>ATK:</strong> {fmt(totalAttack)} | <strong>DEF:</strong> {fmt(totalDefense)}
                        </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        {cards.map((card, index) => (
                            <Grid item key={`${card.id || 'card'}-${index}`}>
                                <PlayingCard
                                    showStats="True"
                                    card={card}
                                    showLevel
                                    size="sm"
                                    onClick={() => handleOpenDialog(card)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Edit Defense Deck Dialog */}
            <Dialog
                open={defenseDialogOpen}
                onClose={() => setDefenseDialogOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        background: '#0b0b0b',
                        color: TOKENS.text,
                        border: TOKENS.border,
                        boxShadow: TOKENS.shadow,
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '0.6px',
                        color: TOKENS.gold,
                    }}
                >
                    Edit Defense Deck
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(212,175,55,0.2)' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: TOKENS.text }}>
                            Pick up to {DEFENSE_LIMIT} cards. Current: {defenseCards.length}/{DEFENSE_LIMIT} —{' '}
                            <strong>ATK:</strong> {fmt(defenseTotalAttack)} | <strong>DEF:</strong> {fmt(defenseTotalDefense)}
                        </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                        {cards.map((card, index) => (
                            <Grid item key={`${card.id || 'card'}-${index}`}>
                                <PlayingCard
                                    showStats="True"
                                    card={card}
                                    showLevel
                                    size="sm"
                                    onClick={() => handleOpenDialog(card)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Snackbar for deck limits */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={closeSnack}
                    severity="warning"
                    variant="filled"
                    sx={{
                        width: '100%',
                        background: TOKENS.chipBg,
                        color: TOKENS.gold,
                        border: TOKENS.border,
                    }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default CardManager;
